import React, { useEffect, useState } from "react";
import { useNavigate, Outlet, useLocation, Link } from "react-router-dom";
import SupportChat from "./common/SupportChat";
import { isMainlandChina } from "../utils/region";

// Language is picked automatically — there's no switch. 中文 for visitors in
// mainland China (same time-zone check that picks WeChat Pay / Alipay, see
// utils/region.js) or whose browser language is Chinese; English otherwise.
// Pages read it from the Outlet context and keep their own en/zh text.
function detectLang() {
  if (isMainlandChina()) return "zh";
  return (navigator.language || "").toLowerCase().startsWith("zh") ? "zh" : "en";
}

const text = {
  en: {
    badgeBlockchain: "Blockchain-Secured Legal Documents",
    badgeAi: "Run by AI Brain — Monitored 24/7",
    logout: "Logout",
    getStarted: "Get Started",
    encryption: "AES-256 Encryption",
    privacy: "Data Privacy",
    support: "24/7 Support",
    terms: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    contact: "Contact Us",
    copyright: (year) => `ShadowVault is a product of AfterLight Technologies Inc. © ${year} All rights reserved.`,
  },
  zh: {
    badgeBlockchain: "区块链保护的法律文件",
    badgeAi: "AI 智能运维 — 全天候监控",
    logout: "退出登录",
    getStarted: "立即开始",
    encryption: "AES-256 加密",
    privacy: "数据隐私",
    support: "全天候支持",
    terms: "服务条款",
    privacyPolicy: "隐私政策",
    contact: "联系我们",
    copyright: (year) => `ShadowVault 是 AfterLight Technologies Inc. 旗下产品。© ${year} 保留所有权利。`,
  },
};

export default function Layout({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [supportOpen, setSupportOpen] = useState(false);
  const [lang] = useState(detectLang);
  const t = text[lang];

  useEffect(() => {
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  }, [lang]);

  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;

  const handleLogout = () => {
    onLogout(); // ✅ now defined
    navigate("/login", { replace: true });
  };


  // Routes that should be centered
  const centeredRoutes = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/",
  ];

  const centerContent = centeredRoutes.some((path) =>
    location.pathname.startsWith(path)
  );

  const isMyFiles = location.pathname.startsWith("/myfiles");

  return (
    <div className={`bg-dark-bg text-white flex flex-col ${isMyFiles ? "h-screen overflow-hidden" : "min-h-screen"}`}>
      {/* ---------- Header ---------- */}
      <header className="flex items-center justify-between px-8 py-4 border-b border-dark-border bg-dark-bg">
        <div
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-2xl font-bold text-primary cursor-pointer hover:opacity-80 transition"
        >
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-dark-bg font-bold">⛓️</span>
          </div>
          <span>ShadowVault</span>
        </div>

        <nav className="hidden md:flex items-center gap-3">
          {!isMyFiles && (
            <>
              <div className="inline-flex items-center gap-2 bg-primary bg-opacity-10 border border-primary border-opacity-30 rounded-full px-4 py-2 text-sm text-primary font-medium">
                <span>⛓️</span> {t.badgeBlockchain}
              </div>
              <div className="inline-flex items-center gap-2 bg-primary bg-opacity-10 border border-primary border-opacity-30 rounded-full px-4 py-2 text-sm text-primary font-medium">
                <span>🧠</span> {t.badgeAi}
              </div>
            </>
          )}
        </nav>

        <div className="flex items-center gap-4">
          {/* ✅ Auth-aware button */}
          {isLoggedIn ? (
            <button
              onClick={handleLogout}
              className="px-5 py-2 text-sm font-medium bg-primary text-dark-bg hover:bg-primary-dark rounded-lg transition font-semibold"
            >
              {t.logout}
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="px-5 py-2 text-sm font-medium bg-primary text-dark-bg hover:bg-primary-dark rounded-lg transition font-semibold"
            >
              {t.getStarted}
            </button>
          )}
        </div>
      </header>

      {/* ---------- Main ---------- */}
      <main
        className={`flex flex-1 ${
          isMyFiles
            ? "overflow-hidden"
            : centerContent
            ? "px-6 items-center justify-center overflow-y-auto"
            : "overflow-y-auto"
        }`}
      >
        <Outlet context={{ lang }} />
      </main>

      {/* ---------- Footer ---------- */}
      <footer className="py-8 border-t border-dark-border bg-dark-bg">
        <div className="max-w-6xl mx-auto px-8 grid grid-cols-2 md:grid-cols-3 gap-4 text-center md:text-left text-sm text-gray-400 mb-6">
          <div className="flex items-center justify-center md:justify-start gap-2"><span>🔐</span> {t.encryption}</div>
          <div className="flex items-center justify-center md:justify-start gap-2"><span>🌐</span> {t.privacy}</div>
          <button
            onClick={() => setSupportOpen(true)}
            className="flex items-center justify-center md:justify-start gap-2 hover:text-primary transition cursor-pointer"
          >
            <span>📞</span> {t.support}
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-8 flex items-center justify-center gap-4 text-xs text-gray-500 mb-4">
          <Link to="/terms" className="hover:text-primary transition">{t.terms}</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-primary transition">{t.privacyPolicy}</Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-primary transition">{t.contact}</Link>
        </div>
        <div className="text-center text-gray-500 text-xs border-t border-dark-border pt-6">
          {t.copyright(new Date().getFullYear())}
        </div>
      </footer>

      {/* Unmounted on close (not just hidden) so reopening starts a fresh conversation */}
      {supportOpen && <SupportChat onClose={() => setSupportOpen(false)} />}
    </div>
  );
}
